import useURLParams from "@/hooks/use-url-params";
import { useCallback, useMemo, useState } from "react";
import { FilterHandler, FilterProps, Filter, FilterData } from "../shared/types";

export interface useFiltersProps {
    filters: FilterProps[],
    useURL?: boolean
}

export interface useFiltersReturn {
    filters: Filter[],
    filterData: FilterData,
    setFilterData: (newFilterData: FilterData) => void,
    getCriteria: () => Promise<string[]>
}

function useConditionalState(useURL: boolean, initialValue: any) {
    const _useURL = useMemo(() => useURL, []);
    if (_useURL) {
        return useURLParams("search", initialValue);
    } else {
        return useState(initialValue);
    }
}

export default function useFilters({
    filters: receivedFilters,
    useURL = true
}: useFiltersProps): useFiltersReturn {
    const _filterHandler = useMemo(() => new FilterHandler(receivedFilters), [receivedFilters]);

    //Calculate initial filterData depending of received filters
    const _initialfilterData = useMemo(() => _filterHandler.getInitialValues(), [_filterHandler]);

    //Create state managers
    const [_filterData, _SetFilterData] = useConditionalState(useURL, _initialfilterData);

    //Define return filter data
    const filterData = useMemo(() => ({ ..._initialfilterData, ..._filterData }), [_initialfilterData, _filterData])

    //Define set Filter data method
    const setFilterData = useCallback((newFilterData: any) => {
        _SetFilterData(_filterHandler.parse(newFilterData))
    }, [_filterHandler]);

    //Get Criteria List
    const getCriteria = useCallback(() => _filterHandler.getCriteria(_filterData), [_filterData, _filterHandler])

    //Construct complete filter list
    const filters = useMemo(() => _filterHandler.getFilters(_filterData), [_filterData, _filterHandler]);

    return useMemo(() => ({
        filters,
        filterData,
        setFilterData,
        getCriteria
    }), [filters, filterData, setFilterData, getCriteria]);
}
